import React, { ReactNode, useRef } from "react";
import { motion, useReducedMotion, useScroll, useTransform, Variants } from "framer-motion";

export const viewportOnce = { once: true, amount: 0.18 } as const;

export const fadeUp: Variants = {
  hidden: { opacity: 0, y: 34 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.7, ease: [0.22, 1, 0.36, 1] } },
};

export const fadeIn: Variants = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { duration: 0.75, ease: "easeOut" } },
};

export const staggerContainer: Variants = {
  hidden: {},
  visible: { transition: { staggerChildren: 0.1, delayChildren: 0.08 } },
};

export const cardReveal: Variants = {
  hidden: { opacity: 0, y: 36, rotate: -1.8, scale: 0.975 },
  visible: { opacity: 1, y: 0, rotate: 0, scale: 1, transition: { duration: 0.75, ease: [0.22, 1, 0.36, 1] } },
};

export const Reveal = ({ children, className = "", delay = 0 }: { children: ReactNode; className?: string; delay?: number }) => {
  const reduceMotion = useReducedMotion();
  return <motion.div
    className={className}
    variants={reduceMotion ? undefined : fadeUp}
    initial={reduceMotion ? false : "hidden"}
    whileInView={reduceMotion ? undefined : "visible"}
    viewport={viewportOnce}
    transition={{ delay }}
  >{children}</motion.div>;
};

export const ParallaxImage = ({ src, alt, className = "", eager = false }: { src: string; alt: string; className?: string; eager?: boolean }) => {
  const ref = useRef<HTMLDivElement>(null);
  const reduceMotion = useReducedMotion();
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const parallaxY = useTransform(scrollYProgress, [0, 1], ["-5%", "5%"]);

  return <div ref={ref} className={`parallax-media ${className}`}>
    <motion.img
      src={src}
      alt={alt}
      loading={eager ? "eager" : "lazy"}
      decoding="async"
      style={{ y: reduceMotion ? 0 : parallaxY }}
    />
  </div>;
};

export const MotionCard = ({ children, className }: { children: ReactNode; className: string }) => {
  const reduceMotion = useReducedMotion();
  return <motion.article
    className={className}
    variants={reduceMotion ? undefined : cardReveal}
    whileHover={reduceMotion ? undefined : { y: -8, rotateX: 1.5, rotateY: -1.5, transition: { duration: 0.25 } }}
  >{children}</motion.article>;
};
